import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { SummaryAction } from "@/types";

interface Props {
  meetingId: string;
  meetingTitle: string | null;
  actions: SummaryAction[];
}

const STORAGE_KEY = (meetingId: string) => `redi_action_done:${meetingId}`;

function actionLine(a: SummaryAction): string {
  const meta = [a.who, a.deadline].filter(Boolean).join(" · ");
  return meta ? `${a.what} (${meta})` : a.what;
}

function actionsMarkdown(title: string | null, actions: SummaryAction[]): string {
  const header = title ? `# ${title}\n\n` : "";
  const body = actions
    .map((a) => `- [ ] ${actionLine(a)}`)
    .join("\n");
  return header + body + "\n";
}

export function ActionsExport({ meetingId, meetingTitle, actions }: Props) {
  const { t } = useTranslation();
  const [done, setDone] = useState<Record<number, boolean>>({});
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY(meetingId));
      if (raw) setDone(JSON.parse(raw));
    } catch {}
  }, [meetingId]);

  const persist = (next: Record<number, boolean>) => {
    setDone(next);
    try {
      localStorage.setItem(STORAGE_KEY(meetingId), JSON.stringify(next));
    } catch {}
  };

  const toggle = (idx: number) => {
    persist({ ...done, [idx]: !done[idx] });
  };

  const copy = async () => {
    await navigator.clipboard.writeText(actionsMarkdown(meetingTitle, actions));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const downloadMd = () => {
    const blob = new Blob([actionsMarkdown(meetingTitle, actions)], {
      type: "text/markdown",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(meetingTitle ?? "actions").replace(/[^\w-]+/g, "_")}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const emailUrl = () => {
    const subject = encodeURIComponent(
      meetingTitle
        ? t("actionsExport.emailSubject", { title: meetingTitle })
        : t("actionsExport.emailSubjectFallback")
    );
    const body = encodeURIComponent(actionsMarkdown(meetingTitle, actions));
    return `mailto:?subject=${subject}&body=${body}`;
  };

  if (actions.length === 0) return null;

  return (
    <div className="mb-4">
      <div className="bg-redi-surface/60 rounded-lg p-3">
        {actions.map((action, idx) => {
          const completed = done[idx] === true;
          return (
            <button
              key={idx}
              onClick={() => toggle(idx)}
              className="flex w-full items-start gap-2 py-1 text-left"
            >
              <span
                className={`mt-0.5 inline-block w-4 h-4 rounded-full border-2 ${
                  completed
                    ? "bg-redi-success border-redi-success"
                    : "border-redi-primary"
                }`}
              />
              <span className="flex-1">
                <span
                  className={`block text-sm ${
                    completed
                      ? "text-redi-muted line-through"
                      : "text-redi-text"
                  }`}
                >
                  {action.what}
                </span>
                <span className="block text-redi-muted text-xs">
                  {action.who}
                  {action.deadline ? ` · ${action.deadline}` : ""}
                </span>
              </span>
            </button>
          );
        })}
      </div>

      <div className="flex flex-wrap gap-2 mt-3">
        <button
          onClick={copy}
          className="flex-1 min-w-[120px] bg-redi-primary text-white text-sm font-semibold py-2 rounded-lg hover:bg-redi-primary/80"
        >
          {copied ? t("share.copied") : t("actionsExport.copy")}
        </button>
        <button
          onClick={downloadMd}
          className="flex-1 min-w-[120px] bg-redi-surface text-redi-muted hover:text-redi-text text-sm font-semibold py-2 rounded-lg"
        >
          {t("actionsExport.download")}
        </button>
        <a
          href={emailUrl()}
          className="flex-1 min-w-[120px] bg-redi-surface text-redi-muted hover:text-redi-text text-sm font-semibold py-2 rounded-lg text-center"
        >
          {t("actionsExport.email")}
        </a>
      </div>
    </div>
  );
}
